import { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";

const DISMISS_KEY = "myazbnb_install_dismissed";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export const InstallPrompt = ({ appName = "MYAZBNB" }) => {
  const { t } = useLang();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    // Android/desktop Chrome: capture the native install prompt
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari has no beforeinstallprompt — show manual instructions on mobile Safari
    const iosSafari = isIos() && /safari/i.test(window.navigator.userAgent) && !/crios|fxios/i.test(window.navigator.userAgent);
    let timer;
    if (iosSafari) {
      timer = setTimeout(() => { setIosHint(true); setShow(true); }, 2500);
    }

    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-prompt"
      className="lg:hidden fixed bottom-4 inset-x-3 z-[60] rounded-2xl border border-border bg-card/95 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.18)] p-4 animate-fade-up"
    >
      <button
        onClick={dismiss}
        data-testid="install-prompt-dismiss"
        aria-label={t("Dismiss")}
        className="absolute top-2.5 right-2.5 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:bg-accent"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <span className="flex-shrink-0 h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
          <Download className="h-5 w-5 text-primary" strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <div className="font-semibold text-foreground text-sm">{t("Add")} {appName} {t("to your home screen")}</div>
          {iosHint ? (
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {t("Tap")} <Share className="inline h-3.5 w-3.5 -mt-0.5 text-primary" /> {t("in Safari, then")} <span className="font-medium text-foreground">"{t("Add to Home Screen")}"</span> <Plus className="inline h-3.5 w-3.5 -mt-0.5 text-primary" />
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {t("Book faster next time — one tap from your phone, just like an app.")}
            </p>
          )}
        </div>
      </div>

      {!iosHint && (
        <Button
          onClick={install}
          data-testid="install-prompt-install"
          className="w-full rounded-full bg-primary hover:bg-primary/90 h-10 mt-3"
        >
          <Download className="h-4 w-4 mr-2" /> {t("Add to Home Screen")}
        </Button>
      )}
    </div>
  );
};
